import { useState } from "react";
import { Badge, Box, Divider, Grid2, Popover, styled, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import CloseButton from "../../../Components/CloseButton";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";
import TextWithCopy from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import DotSeparator from "../../../Components/DotSeparator";

const ActiveButtonBadge = styled(Badge)(() => ({
    marginRight: '4px',
    "& .MuiBadge-badge": {
        position: 'relative',
        backgroundColor: 'currentColor',
        color: 'currentColor',
        width: "10px",
        height: "10px",
        minWidth: "10px",
        borderRadius: "50%",
        transform: 'none',
    },
}));

const styles = {
    popoverPaper: {
        width: 'fit-content',
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        boxShadow: '-2px -2px 4px 0px rgba(151, 151, 151, 0.25), 2px 2px 4px 0px rgba(151, 151, 151, 0.25)',
    },
};

/**
 * Custom platform "About" popover for bus device headers.
 *
 * Based on DigitalTwinV3/components/Header/BusDeviceInfoPopover with four
 * fields removed: Project, Chassis Number, Bus Type, Protocol.
 */
export default function CustomBusDeviceInfoPopover({ batteryInfo, vehicleInfo, iotState }) {
    const { t } = useTranslation(['common', 'digital_twin']);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openViaClick, setOpenViaClick] = useState(false);
    const open = Boolean(anchorEl);

    const handlePopoverHoverOn = (event) => setAnchorEl(event.currentTarget);
    const handlePopoverHoverClose = () => { if (!openViaClick) setAnchorEl(null); };
    const handlePopoverClick = (event) => { setOpenViaClick(true); setAnchorEl(event.currentTarget); };
    const handlePopoverClose = () => { setOpenViaClick(false); setAnchorEl(null); };

    return (
        <>
            <Typography
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverHoverOn}
                onClick={handlePopoverClick}
                onMouseLeave={handlePopoverHoverClose}
                sx={{
                    cursor: 'pointer',
                    lineHeight: '16px',
                    textDecoration: openViaClick ? 'underline' : '',
                    '&:hover': { textDecoration: 'underline' },
                }}
                variant="fw600s16lh24c444B51"
            >
                {vehicleInfo.licensePlateNo}
            </Typography>

            <Popover
                id="mouse-over-popover"
                sx={{ pointerEvents: openViaClick ? '' : 'none' }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: styles.popoverPaper } }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <Box sx={{ minWidth: '350px', p: 3 }}>
                    <Grid2 container justifyContent="space-between" alignItems="center">
                        <Typography variant="fw600s16c000000" sx={{ lineHeight: '24px' }}>{t("common:about")}</Typography>
                        {openViaClick && <CloseButton onClick={handlePopoverClose} />}
                    </Grid2>

                    <Grid2 sx={{ mt: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ActiveButtonBadge
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: batteryInfo.pingStatus?.toLowerCase() === 'active' ? '#09B820' : '#8F8F8F',
                                        color: batteryInfo.pingStatus?.toLowerCase() === 'active' ? '#09B820' : '#8F8F8F',
                                    },
                                }}
                                variant="dot"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            />
                            <Typography variant="fw400s12c000000" sx={{ lineHeight: '24px' }}>
                                {batteryInfo.pingStatus?.toLowerCase() === 'active' ? t("common:online") : t("common:offline")}
                            </Typography>
                        </Box>
                        <DotSeparator />
                        <Box>
                            <Typography variant="fw400s12c000000" sx={{ lineHeight: '24px' }}>{t('digital_twin:last_pinged')}&nbsp;:&nbsp;</Typography>
                            {batteryInfo.lastPingedTime ? (
                                <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastPingedTime)} placement="bottom">
                                    <Typography variant="fw500s12lh24c444B51">{getMetricTimeStamp(batteryInfo.lastPingedTime, 'relative')}</Typography>
                                </PrimaryTooltip>
                            ) : (
                                <Typography variant="fw500s12lh24c444B51">NA</Typography>
                            )}
                        </Box>
                    </Grid2>

                    <Divider sx={{ borderWidth: '0.5px', borderColor: '#E9E9E9' }} />

                    <Grid2 container gap={1} sx={{ marginTop: 1.5, paddingTop: 1, paddingBottom: 1, flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("digital_twin:country")}:</Typography>
                            <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo.originCountry ?? '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("digital_twin:state")}:</Typography>
                            <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo.originState ?? '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("digital_twin:city")}:</Typography>
                            <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo.originCity ?? '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("digital_twin:depot")}:</Typography>
                            <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo.originDepo ?? '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("common:device_id")}:</Typography>
                            {batteryInfo.deviceID ? (
                                <TextWithCopy text={batteryInfo.deviceID} type="Device ID" textVariant="fw400s14c000000" />
                            ) : (
                                <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{'-'}</Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>{t("digital_twin:chassis_number")}:</Typography>
                            {vehicleInfo.vin ? (
                                <TextWithCopy text={vehicleInfo.vin} type="Chassis Number" textVariant="fw400s14c000000" />
                            ) : (
                                <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{'-'}</Typography>
                            )}
                        </Box>
                    </Grid2>
                </Box>
            </Popover>
        </>
    );
}
